/**
 * Timers that keep firing in background tabs.
 *
 * Chrome throttles main-thread timers in backgrounded tabs to 1/s, and to
 * 1/min after 5 minutes ("intensive throttling"). Dedicated-worker timers
 * are exempt, so the SDK routes its internal timers (reconnect backoff,
 * handshake timeouts) through a tiny inline worker. Falls back to plain
 * timers where Worker/Blob is unavailable (tests, old browsers).
 */

type TimerCallback = () => void;

const WORKER_SOURCE = `
  const timers = new Map();
  self.onmessage = (e) => {
    const { op, id, ms } = e.data;
    if (op === "timeout") {
      timers.set(id, setTimeout(() => { timers.delete(id); self.postMessage({ id }); }, ms));
    } else if (op === "interval") {
      timers.set(id, setInterval(() => self.postMessage({ id }), ms));
    } else if (op === "clear") {
      const t = timers.get(id);
      clearTimeout(t);
      clearInterval(t);
      timers.delete(id);
    }
  };
`;

class WorkerTimers {
  private worker: Worker | null = null;
  private workerFailed = false;
  private callbacks = new Map<number, { cb: TimerCallback; once: boolean }>();
  private fallbackHandles = new Map<number, ReturnType<typeof setTimeout>>();
  private nextId = 1;

  private ensureWorker(): Worker | null {
    if (this.worker) return this.worker;
    if (this.workerFailed) return null;
    if (typeof Worker === "undefined" || typeof Blob === "undefined" || typeof URL === "undefined") {
      this.workerFailed = true;
      return null;
    }
    try {
      const url = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: "text/javascript" }));
      this.worker = new Worker(url);
      URL.revokeObjectURL(url);
      this.worker.onmessage = (e: MessageEvent<{ id: number }>) => {
        const entry = this.callbacks.get(e.data.id);
        if (!entry) return;
        if (entry.once) this.callbacks.delete(e.data.id);
        entry.cb();
      };
    } catch {
      this.worker = null;
      this.workerFailed = true;
    }
    return this.worker;
  }

  setTimeout(cb: TimerCallback, ms: number): number {
    const id = this.nextId++;
    const worker = this.ensureWorker();
    if (worker) {
      this.callbacks.set(id, { cb, once: true });
      worker.postMessage({ op: "timeout", id, ms });
    } else {
      const handle = globalThis.setTimeout(() => {
        this.fallbackHandles.delete(id);
        cb();
      }, ms);
      this.fallbackHandles.set(id, handle);
    }
    return id;
  }

  setInterval(cb: TimerCallback, ms: number): number {
    const id = this.nextId++;
    const worker = this.ensureWorker();
    if (worker) {
      this.callbacks.set(id, { cb, once: false });
      worker.postMessage({ op: "interval", id, ms });
    } else {
      const handle = globalThis.setInterval(cb, ms);
      this.fallbackHandles.set(id, handle);
    }
    return id;
  }

  clear(id: number): void {
    this.callbacks.delete(id);
    const handle = this.fallbackHandles.get(id);
    if (handle !== undefined) {
      globalThis.clearTimeout(handle);
      globalThis.clearInterval(handle);
      this.fallbackHandles.delete(id);
    }
    this.worker?.postMessage({ op: "clear", id });
  }
}

export const timers = new WorkerTimers();
